import { request } from '@mairistem/tools';
import React from 'react';

import { postFacturesEtatMfo } from '@/routes';
import type { EtatMfoReponseType } from '@/types';

interface EtatMFOResultatType {
  // Identifiants des factures payées en ligne parmi celles qui ont été interrogées.
  payees: Set<number>;
  // Vrai si MFO n'a pas répondu : `payees` est alors vide sans que cela signifie « rien n'est payé ».
  indisponible: boolean;
}

interface UseEtatMFOResult {
  // Identifiants des factures déjà payées en ligne. Un Set : la modale teste l'appartenance
  // pour chaque ligne de son tableau.
  facturesPayees: Set<number>;
  // Vrai si MFO n'a pas répondu : l'état est indéterminé, mais l'encaissement reste possible.
  mfoIndisponible: boolean;
  loading: boolean;
  // Relance l'interrogation et rend son résultat à l'appelant, sans attendre un nouveau rendu.
  verifierMaintenant: (identifiants: number[]) => Promise<EtatMFOResultatType>;
}

/**
 * Hook de contrôle du paiement en ligne, avant encaissement au guichet.
 *
 * Interroge MFO pour savoir lesquelles des factures affichées ont déjà été payées sur Mon Espace
 * Famille, afin d'avertir le régisseur avant qu'il ne saisisse un règlement.
 *
 * Le contrôle est un confort, pas un verrou comptable : une indisponibilité de MFO n'empêche jamais
 * d'encaisser, elle lève seulement `mfoIndisponible`. Volontairement sans toast d'erreur — ce n'est
 * pas l'échec d'une action de l'utilisateur, et un toast rouge laisserait croire à un problème
 * d'encaissement.
 *
 * Deux usages complémentaires :
 *  - à l'ouverture, l'effet interroge MFO et alimente `facturesPayees` pour l'affichage ;
 *  - à la validation, `verifierMaintenant` réinterroge et **retourne** le résultat. Relire
 *    `facturesPayees` ne suffirait pas : c'est la valeur du rendu en cours, donc celle d'avant
 *    l'appel, et un paiement survenu entre-temps y serait invisible.
 */
const useEtatMFO = (identifiantsFactures: number[]): UseEtatMFOResult => {
  const [facturesPayees, setFacturesPayees] = React.useState<Set<number>>(new Set());
  const [mfoIndisponible, setMfoIndisponible] = React.useState(false);
  const [loading, setLoading] = React.useState(false);

  const verifierMaintenant = React.useCallback(
    (identifiants: number[]): Promise<EtatMFOResultatType> => {
      // Rien à interroger : ce n'est pas une indisponibilité, c'est une certitude.
      if (identifiants.length === 0) {
        return Promise.resolve({ payees: new Set<number>(), indisponible: false });
      }

      setLoading(true);
      setMfoIndisponible(false);

      return (
        request
          .post(postFacturesEtatMfo(), { identifiantsFactures: identifiants })
          .then((reponse: EtatMfoReponseType): EtatMFOResultatType => {
            if (!reponse?.mfoInterroge) {
              return { payees: new Set<number>(), indisponible: true };
            }

            return {
              payees: new Set(
                (reponse.factures ?? [])
                  .filter((etat) => etat.payeeSurMFO)
                  .map((etat) => etat.identifiantFacture),
              ),
              indisponible: false,
            };
          })
          .catch((): EtatMFOResultatType => ({ payees: new Set<number>(), indisponible: true }))
          // La mise à jour de l'état est faite ici, après la branche d'erreur, pour qu'un seul
          // endroit la porte : l'affichage et la valeur rendue à l'appelant ne peuvent pas diverger.
          .then((resultat: EtatMFOResultatType) => {
            setFacturesPayees(resultat.payees);
            setMfoIndisponible(resultat.indisponible);
            setLoading(false);

            return resultat;
          })
      );
    },
    [],
  );

  // Dépendance primitive : `identifiantsFactures` est un tableau recréé à chaque rendu du parent.
  // S'en servir directement relancerait l'effet en boucle.
  const cleFactures = identifiantsFactures.join(',');

  React.useEffect(() => {
    if (cleFactures === '') {
      return;
    }

    verifierMaintenant(cleFactures.split(',').map(Number));
  }, [cleFactures, verifierMaintenant]);

  return { facturesPayees, mfoIndisponible, loading, verifierMaintenant };
};

export default useEtatMFO;
